import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { EngagedUser, ORDER_STATUS } from '@root/src/types';
import config from '@root/src/config';
import downloadPDF from '@utils/downloadPDF';
import api from '@services/api';
import { useRequestHandler } from '@services/api/handlers';
import { ICON_NAME } from '@features/ui/components/Icon';
import {
  selectEventCategory,
  selectEventEngagedByCategory,
  selectEventAthletesCountForCategory,
} from '@features/eventDetail/slices/eventDetailServerSlice';
import List, { TABLE_ITEM_HEADER_TYPE, TableDefinition } from '@features/ui/components/List';
import { getQueryEventId, useShouldHighlight } from '@features/eventDetail/utils';
import {
  formatLocality,
  getAthleteAndTeamValue,
  getPersonName,
} from '@features/eventDetail/templates/EventListPageTemplate';
import CountryFlag from '@features/ui/components/CountryFlag';

interface EngagedCategoryListProps {
  categoryId: number;
  isLoading?: boolean;
}

const EngagedCategoryList = ({ categoryId, isLoading }: EngagedCategoryListProps) => {
  const { t } = useTranslation();
  const requestHandler = useRequestHandler();
  const router = useRouter();

  const shouldHighlight = useShouldHighlight();

  const eventId = getQueryEventId(router.query);
  const category = useSelector(selectEventCategory(eventId, categoryId));
  const engaged = useSelector(selectEventEngagedByCategory(eventId, categoryId));
  const engagedCount = useSelector(selectEventAthletesCountForCategory(eventId, categoryId));

  const tableDefinition: TableDefinition<EngagedUser> = [
    {
      size: {
        desktop: '70px',
        tablet: '70px',
        mobile: 'minmax(30px,70px)',
      },
      name: t('eventResultsList_headerBib.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        isBold: true,
        content: engagedPerson.bib,
      }),
    },
    {
      size: {
        desktop: '1.5fr',
        tablet: 'minmax(80px, 1fr)',
        mobile: null,
      },
      name: t(category.team?.nbMembers > 1 ? 'eventList_headerClubTeamName.message' : 'eventList_headerSoloClubName.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        content: engagedPerson.teamName,
      }),
    },
    {
      size: {
        desktop: '2fr',
        tablet: '2fr',
        mobile: '2fr',
      },
      name: t('eventList_headerName.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        isBold: true,
        content: getAthleteAndTeamValue({
          data: engagedPerson,
          getKey: getPersonName,
          t,
        }),
      }),
    },
    {
      size: {
        desktop: '50px',
        tablet: '50px',
        mobile: null,
      },
      name: t('eventList_headerYear.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        content: getAthleteAndTeamValue({
          data: engagedPerson,
          key: 'birthDateObj.year',
        }),
      }),
    },
    {
      size: {
        desktop: '1.5fr',
        tablet: '1.5fr',
        mobile: null,
      },
      name: t('eventList_headerLocality.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        content: formatLocality(getAthleteAndTeamValue({
          data: engagedPerson,
          key: 'legAddress.locality',
        })),
      }),
    },
    {
      size: {
        desktop: '60px',
        tablet: null,
        mobile: null,
      },
      name: t('eventList_headerDepartement.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        content: getAthleteAndTeamValue({
          data: engagedPerson,
          key: 'legAddress.department',
        }),
      }),
    },
    {
      size: {
        desktop: '75px',
        tablet: null,
        mobile: null,
      },
      name: t('eventList_headerNationality.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        content: (
          <CountryFlag nationality={getAthleteAndTeamValue({
            data: engagedPerson,
            key: 'nationality',
          })}
          />
        ),
      }),
    },
    {
      size: {
        desktop: '2fr',
        tablet: null,
        mobile: null,
      },
      name: t('eventList_headerCategoryName.message'),
      renderCell: () => ({
        type: TABLE_ITEM_HEADER_TYPE.TEXT,
        content: category?.name,
      }),
    },
    {
      size: {
        desktop: '50px',
        tablet: null,
        mobile: null,
      },
      name: t('eventList_headerCategoryPAI.message'),
      renderCell: (engagedPerson) => ({
        type: TABLE_ITEM_HEADER_TYPE.ICON,
        iconName: engagedPerson.orderStatus === ORDER_STATUS.PAID ? ICON_NAME.check : ICON_NAME.clear,
        iconColor: engagedPerson.orderStatus === ORDER_STATUS.PAID ? 'var(--color-blue)' : 'var(--color-red)',
      }),
    },
  ];

  const handleDownloadClick = () => {
    const downloadParams = {
      categoryId: category._idMso,
      locale: router.locale,
    };
    const downloadEndpoint = eventId > config.customHandlings.euroCategoriesIds
      ? api.events.categories.engaged.download
      : api.results.engaged.categories.download;

    const request = requestHandler({ request: downloadEndpoint(downloadParams) });
    const fileName = `${t('engagedCategoryList_titleExportSuffixNameFile.message')}-${category.name}.csv`;
    downloadPDF(request, fileName);
  };

  return (
    <List
      items={engaged}
      listHeaderName={category?.name}
      listHeaderCount={engagedCount}
      listHeaderIcon={category.team?.nbMembers > 0 ? ICON_NAME.iconGroups : ICON_NAME.person}
      listHeaderDownloadClick={handleDownloadClick}
      tableItemShouldHighlight={(engagedPerson) => shouldHighlight(engagedPerson.athlete._idMso)}
      tableDefinition={tableDefinition}
      uniqueId={category._idMso}
      isLoading={isLoading}
    />
  );
};

export default EngagedCategoryList;
